import * as XLSX from 'xlsx';
import { parseARaging } from './index';

// Only read visible sheets
function getVisibleSheets(wb) { // eslint-disable-line
  return wb.SheetNames.filter(name => {
    const ws = wb.Sheets[name];
    return !ws['!hidden'];
  });
}

function safeNum(v) {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'string' && v.startsWith('=')) return 0;
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

function rowsFromSheet(wb, sheetName) {
  const ws = wb.Sheets[sheetName];
  if (!ws) return [];
  return XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
}

// ─── MIS P&L PARSER ──────────────────────────────────────────────────────────
// Reads Dubai/KSA, Warehouse/Shahalam sheets. Builds Consol by summing them
// in code — never relies on Excel Consol sheet formulas (which XLSX.js can't resolve).
export function parseMIS(buffer) {
  const wb = XLSX.read(buffer, { type: 'array', cellFormula: false, raw: true });
  const result = {};

  // UAE sheets: Dubai, Warehouse
  // Saudi sheets: KSA, F F-Shahalam (or Shahalam)
  const sheetMappings = [
    { keys: ['Dubai', 'KSA'], outputKey: 'Dubai' },
    { keys: ['Warehouse', 'F F-Shahalam', 'Shahalam', 'F-Shahalam'], outputKey: 'Warehouse' },
  ];

  const parseSheet = (sheetName) => {
    const rows = rowsFromSheet(wb, sheetName);
    if (!rows.length) return null;

    let headerIdx = rows.findIndex(r =>
      r && r.some(c => typeof c === 'string' && c.toString().toUpperCase().includes('PARTICULARS'))
    );
    if (headerIdx === -1) headerIdx = 2;

    const headers = rows[headerIdx] || [];
    const months = [];
    const monthCols = [];
    let ytdCol = -1;

    headers.forEach((h, i) => {
      if (!h) return;
      const s = h.toString().toUpperCase().trim();
      if (['JAN','FEB','MAR','APR','MAY','JUN','JUNE','JUL','JULY','AUG','SEP','SEPT','OCT','NOV','DEC'].includes(s)) {
        months.push(s === 'JUNE' ? 'JUN' : s === 'JULY' ? 'JUL' : s === 'SEPT' ? 'SEP' : s);
        monthCols.push(i);
      } else if (s === 'YTD') {
        ytdCol = i;
      }
    });

    const lines = [];
    for (let i = headerIdx + 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || !row[0]) continue;
      const label = row[0].toString().trim();
      if (!label) continue;

      const values = monthCols.map(c => safeNum(row[c]));
      const ytd = ytdCol >= 0 ? safeNum(row[ytdCol]) : values.reduce((s, v) => s + v, 0);

      lines.push({ label, values, ytd, months });
    }

    return { months, monthCols, ytdCol, lines };
  };

  sheetMappings.forEach(({ keys, outputKey }) => {
    const sheetName = keys.find(k => wb.SheetNames.includes(k));
    if (!sheetName) return;
    const parsed = parseSheet(sheetName);
    if (parsed) result[outputKey] = parsed;
  });

  // Build Consol by summing Dubai + Warehouse in code
  // This is more reliable than reading Excel's Consol sheet (formula caching issues)
  if (result.Dubai || result.Warehouse) {
    const base = result.Dubai || result.Warehouse;
    const other = result.Dubai ? result.Warehouse : null;

    const consolLines = base.lines.map(baseLine => {
      const otherLine = other?.lines.find(l => l.label === baseLine.label);
      const isPct = baseLine.label.includes('%');

      if (isPct) {
        // Don't sum percentages — recalculate below after summing raw values
        return { ...baseLine, _skip: true };
      }

      const values = baseLine.values.map((v, i) => v + (otherLine?.values[i] || 0));
      const ytd = baseLine.ytd + (otherLine?.ytd || 0);
      return { label: baseLine.label, values, ytd, months: baseLine.months };
    });

    // Recalculate GP% and NP% from summed values
    const consolLinesFixed = consolLines.map(line => {
      if (!line._skip) return line;
      const salesLine = consolLines.find(l => l.label === 'Sales' || l.label.toUpperCase() === 'SALES');
      const isGPPct = line.label.toUpperCase().includes('GP %') || line.label.toUpperCase().includes('GP%');
      const isNPPct = line.label.toUpperCase().includes('NP %') || line.label.toUpperCase().includes('NP%');

      if (isGPPct) {
        const gpLine = consolLines.find(l => l.label.toUpperCase().includes('GROSS PROFIT') && !l.label.includes('%'));
        const values = (salesLine?.values || []).map((s, i) => s > 0 ? ((gpLine?.values[i] || 0) / s * 100) : 0);
        const ytd = salesLine?.ytd > 0 ? ((gpLine?.ytd || 0) / salesLine.ytd * 100) : 0;
        return { label: line.label, values, ytd, months: line.months };
      }
      if (isNPPct) {
        const npLine = consolLines.find(l => l.label.toUpperCase().includes('NET PROFIT') && !l.label.includes('%'));
        const values = (salesLine?.values || []).map((s, i) => s > 0 ? ((npLine?.values[i] || 0) / s * 100) : 0);
        const ytd = salesLine?.ytd > 0 ? ((npLine?.ytd || 0) / salesLine.ytd * 100) : 0;
        return { label: line.label, values, ytd, months: line.months };
      }
      return { ...line, _skip: undefined };
    });

    result.Consol = {
      months: base.months,
      monthCols: base.monthCols,
      ytdCol: base.ytdCol,
      lines: consolLinesFixed.filter(l => !l._skip),
      _computed: true, // flag that this was computed, not read from sheet
    };
  }

  // Also try reading the actual Consol sheet as fallback (UAE has a real Consol sheet)
  if (!result.Consol?._computed && wb.SheetNames.includes('Consol')) {
    const parsed = parseSheet('Consol');
    if (parsed && parsed.lines.some(l => l.ytd !== 0)) {
      result.Consol = parsed;
    }
  }

  return result;
}

// ─── ACT VS BUDGET PARSER ────────────────────────────────────────────────────
export function parseActVsBudget(buffer) {
  const wb = XLSX.read(buffer, { type: 'array', cellNF: true });

  // Only read visible sheet - Act-P & L (Consol)
  const sheetName = wb.SheetNames.find(n =>
    !wb.Sheets[n]['!hidden'] &&
    n.toLowerCase().includes('consol')
  ) || wb.SheetNames[0];

  const rows = rowsFromSheet(wb, sheetName);

  // Extract period from row 1
  const periodRow = rows[1] ? rows[1][0] || '' : '';
  const periodMatch = periodRow.toString().match(/([A-Z]+-\d{4})/i);
  const period = periodMatch ? periodMatch[1] : 'Latest';

  // Find header row
  let headerIdx = rows.findIndex(r => r && r[0] && r[0].toString().includes('PARTICULARS'));
  if (headerIdx === -1) headerIdx = 2;

  const lines = [];
  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || !row[0]) continue;
    const label = row[0].toString().trim();
    if (!label) continue;

    const actual = safeNum(row[1]);
    const budget = safeNum(row[2]);
    const variance = actual - budget;
    const isHeader = ['GROSS PROFIT', 'NET PROFIT'].includes(label.toUpperCase());

    lines.push({ label, actual, budget, variance, isHeader });
  }

  return { period, lines };
}

// ─── CUSTOMER GP PARSER ──────────────────────────────────────────────────────
export function parseCustomerGP(buffer) {
  const wb = XLSX.read(buffer, { type: 'array', cellNF: true }); // no cellDates - we parse manually

  // UAE: '2026' for freight, 'WH 2026' for warehouse
  // Saudi: 'Haziq' for freight, 'Shahalam' for warehouse
  const freightSheet = wb.SheetNames.find(n =>
    n === '2026' || n.toLowerCase() === 'haziq' || n.toLowerCase().includes('2026')
  );
  const warehouseSheet = wb.SheetNames.find(n =>
    n === 'WH 2026' || n.toLowerCase() === 'shahalam' || n.toLowerCase().includes('wh')
  );

  const result = { freight: [], warehouse: [], months: [] };

  // Convert Excel serial date number to month string like "JAN-26"
  function excelDateToMonthStr(v) {
    if (v == null) return null;
    // Excel date serial: days since 1900-01-01
    if (typeof v === 'number' && v > 1000) {
      const date = new Date(Math.round((v - 25569) * 86400 * 1000));
      const mon = date.toLocaleString('en', { month: 'short', timeZone: 'UTC' }).toUpperCase();
      const yr = date.getUTCFullYear().toString().slice(2);
      return `${mon}-${yr}`;
    }
    // Already a string like "Jan-26" or "2026-01-01"
    if (typeof v === 'string') {
      const m = v.match(/(\w{3})[- ](\d{2,4})/);
      if (m) return `${m[1].toUpperCase()}-${m[2].slice(-2)}`;
      // ISO date string
      const iso = v.match(/(\d{4})-(\d{2})/);
      if (iso) {
        const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
        return `${months[parseInt(iso[2]) - 1]}-${iso[1].slice(2)}`;
      }
    }
    return null;
  }

  const parseSheet = (sheetName) => {
    if (!sheetName) return [];
    const rows = rowsFromSheet(wb, sheetName);
    if (!rows.length) return [];

    const monthRow = rows[2] || [];
    const dataRow = rows[3] || [];
    const cols = [];

    monthRow.forEach((cell, i) => {
      const monthStr = excelDateToMonthStr(cell);
      if (monthStr) {
        cols.push({ month: monthStr, salesCol: i, gpCol: i + 1 });
      }
    });

    let ytdSalesCol = -1, ytdGpCol = -1;
    dataRow.forEach((cell, i) => {
      if (cell && cell.toString().toUpperCase() === 'SALES' &&
          monthRow[i] && monthRow[i].toString().toUpperCase().includes('YTD')) {
        ytdSalesCol = i;
        ytdGpCol = i + 1;
      }
    });

    const customers = [];
    for (let i = 4; i < rows.length; i++) {
      const row = rows[i];
      if (!row || !row[0]) continue;
      const name = row[0].toString().trim();
      if (!name || name.toUpperCase() === 'TOTAL') continue;

      const monthly = cols.map(c => ({
        month: c.month,
        sales: safeNum(row[c.salesCol]),
        gp: safeNum(row[c.gpCol]),
      }));

      const ytdSales = ytdSalesCol >= 0 ? safeNum(row[ytdSalesCol]) : monthly.reduce((s, m) => s + m.sales, 0);
      const ytdGP = ytdGpCol >= 0 ? safeNum(row[ytdGpCol]) : monthly.reduce((s, m) => s + m.gp, 0);
      const gpPct = ytdSales > 0 ? (ytdGP / ytdSales) * 100 : 0;

      customers.push({ name, monthly, ytdSales, ytdGP, gpPct });
    }
    return customers;
  };

  result.freight = parseSheet(freightSheet);
  result.warehouse = parseSheet(warehouseSheet);

  // Get months from freight sheet
  if (freightSheet) {
    const rows = rowsFromSheet(wb, freightSheet);
    const monthRow = rows[2] || [];
    monthRow.forEach(cell => {
      const monthStr = excelDateToMonthStr(cell);
      if (monthStr) result.months.push(monthStr);
    });
  }

  return result;
}

// ─── AJC & WIP PARSER ────────────────────────────────────────────────────────
export function parseAJCWIP(buffer) {
  const wb = XLSX.read(buffer, { type: 'array', cellNF: true });
  const sheetName = wb.SheetNames[0];
  const rows = rowsFromSheet(wb, sheetName);

  const result = { ajc: [], wip: [], revWip: [] };
  let section = null;

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!row) continue;
    const cell0 = (row[0] || '').toString().toUpperCase().trim();
    const cell1 = (row[1] || '').toString().toUpperCase().trim();

    // Saudi format: AJC header, then customer | job number | amount
    if (cell0 === 'AJC') { section = 'ajc'; continue; }
    if (cell0 === 'WIP' && !cell0.includes('REV')) { section = 'wip'; continue; }
    if (cell0.includes('REVENUE') && cell0.includes('WIP')) { section = 'revwip'; continue; }

    // UAE format detection
    if (cell0.includes('AJC') || cell0.includes('ACCRUED')) { section = 'ajc'; continue; }
    if (cell0.includes('WIP') && !cell0.includes('REV') && cell0.length < 10) { section = 'wip'; continue; }

    // Skip header rows
    if (cell0 === 'NAME OF THE CUSTOMER' || cell0 === 'JOB' || cell1 === 'JOB NUMBER') continue;

    if (!section || !row[0]) continue;

    // Saudi format: customer | job number | amount
    // UAE format: job number | customer | ... | amount
    let customer, job, amount, ageDays;

    // Detect format by checking if col1 looks like a job number
    const col1 = (row[1] || '').toString().trim();
    const isJobNumber = col1.match(/^[A-Z].*-\d+/) || col1.match(/^\d{5,}/);

    if (isJobNumber) {
      // Saudi format: customer | job | amount
      customer = (row[0] || '').toString().trim();
      job = col1;
      amount = safeNum(row[2]);
      ageDays = 0;
    } else {
      // UAE format: job | customer | ... | amount | age
      job = (row[0] || '').toString().trim();
      customer = (row[1] || '').toString().trim();
      amount = safeNum(row[3] || row[2]);
      ageDays = safeNum(row[row.length - 1]);
    }

    if (!customer && !job) continue;
    if (amount === 0) continue;

    const entry = { job, customer, amount, ageDays };
    if (section === 'ajc') result.ajc.push(entry);
    else if (section === 'wip') result.wip.push(entry);
    else result.revWip.push(entry);
  }

  result.ajcTotal = result.ajc.reduce((s, r) => s + r.amount, 0);
  result.wipTotal = result.wip.reduce((s, r) => s + r.amount, 0);
  result.revWipTotal = result.revWip.reduce((s, r) => s + r.amount, 0);

  return result;
}

// ─── MONTHLY PACK ASSEMBLER ──────────────────────────────────────────────────
export function assembleMonthlyPack(files) {
  const pack = {};

  if (files.mis_ytd) {
    try { pack.mis_ytd = parseMIS(files.mis_ytd); } catch(e) { console.error('MIS YTD parse error', e); }
  }
  if (files.mis_period) {
    try { pack.mis_period = parseMIS(files.mis_period); } catch(e) { console.error('MIS Period parse error', e); }
  }
  if (files.gp) {
    try { pack.gp = parseCustomerGP(files.gp); } catch(e) { console.error('GP parse error', e); }
  }
  if (files.avb) {
    try { pack.avb = parseActVsBudget(files.avb); } catch(e) { console.error('AvB parse error', e); }
  }
  if (files.ajc_wip) {
    try { pack.ajc_wip = parseAJCWIP(files.ajc_wip); } catch(e) { console.error('AJC/WIP parse error', e); }
  }
  if (files.ar) {
    try { pack.ar_data = parseARaging(files.ar); } catch(e) { console.error('AR parse error', e); }
  }
  if (files.ap) {
    try { pack.ap_data = parseARaging(files.ap); } catch(e) { console.error('AP parse error', e); }
  }

  return pack;
}
